// Front-end-only image support for Message Center image-type messages.
//
// The backend has no image field or upload endpoint yet, so the chosen image
// travels inside the persisted message body as a token: `[e360-img:key]caption
// text`. The key resolves against a curated registry of images deployed under
// public/images/message-center/, which the web server serves to every user.
// When a real upload feature ships, only this token layer needs replacing —
// the registry, resolver and UI stay.
//
// To add a library image: drop the file in public/images/message-center/
// (any web image format — jpg, png, svg, webp) and add a matching entry to
// MESSAGE_IMAGES with its full filename.

export const MESSAGE_TYPE_ID_IMAGE = 3;

export interface IMessageImage {
  key: string;
  label: string;
  src: string;
}

// Every entry must have a matching file in public/images/message-center/.
export const MESSAGE_IMAGES: IMessageImage[] = [
  {
    key: 'before-and-after',
    label: 'Before and After',
    src: '/images/message-center/comparing-thermostat-ui-before-and-after.png',
  },
  // {
  //   key: 'building',
  //   label: 'Commercial Building',
  //   src: '/images/message-center/building.jpg',
  // },
  // {
  //   key: 'solar-farm',
  //   label: 'Solar Farm',
  //   src: '/images/message-center/solar-farm.jpg',
  // },
];

// Shown for legacy messages with no token and for unknown/removed keys.
export const DEFAULT_MESSAGE_IMAGE_SRC = '/images/message-center/default.jpg';

// The bracket form is what gets written: plain text survives any backend
// sanitization. The HTML-comment forms (raw, and HTML-encoded by a sanitizer)
// are still parsed for messages saved before the format switch.
const IMAGE_TOKEN_PATTERNS = [
  /\[e360-img:([\w-]+)\]/,
  /<!--\s*e360-img:([\w-]+)\s*-->/,
  /&lt;!--\s*e360-img:([\w-]+)\s*--&gt;/,
];

export const parseMessageImage = (
  value: string | null | undefined
): { imageKey: string | null; cleanValue: string } => {
  const rawValue = value ?? '';
  for (const pattern of IMAGE_TOKEN_PATTERNS) {
    const match = rawValue.match(pattern);
    if (match) {
      return {
        imageKey: match[1],
        cleanValue: rawValue.replace(pattern, '').trim(),
      };
    }
  }
  return { imageKey: null, cleanValue: rawValue };
};

export const serializeMessageImage = (
  cleanValue: string,
  imageKey: string | null
): string => (imageKey ? `[e360-img:${imageKey}]${cleanValue}` : cleanValue);

export const resolveMessageImageSrc = (imageKey: string | null): string =>
  MESSAGE_IMAGES.find((image) => image.key === imageKey)?.src ??
  DEFAULT_MESSAGE_IMAGE_SRC;
